import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { reuseRows } from '../../../../../components/rows';
import { formatDate, maskCpf, measurementLabelOf, MEASUREMENT_TYPES, STATUS_LABELS, STATUS_ORDER } from '../../../../../domain';
import { CollaboratorsApi } from '../collaborators.api';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../../../components/company-selector/company-selector.presenter';
import { CollaboratorDetailKeys } from '../detail/collaborator-detail.key';
import { sleepReportLinkOf } from '../../sleep-report/sleep-report.link';
import { buildCollaboratorColumns, buildCollaboratorRow, DEFAULT_SORT, MEASUREMENT_FIELDS } from '../collaborator-table';
import { CollaboratorListKeys } from './collaborator-list.key';
import { PeriodSelectorPresenter, shortcutOf } from '../../../../../components/period';
import { COLLABORATOR_SHORTCUTS } from '../collaborators.periods';
import { ColumnChoiceScope, CollaboratorsScope, FilterOptionScope, FilterScope } from './collaborator-list.scope';
const LOG = Logger.get('Mormaii.CollaboratorListPresenter');
// @Inject
const api = CollaboratorsApi.INSTANCE;
/** Como e um `Where` sem nada escondendo linha. */
const UNFILTERED = {};
/** A espera entre a ultima tecla e a busca - a mesma de Empresas e dos Monitoramentos. */
const BUSCA_DEBOUNCE_MS = 400;
/**
 * O valor do filtro CADASTRO quando ele esta ligado.
 *
 * O `ChoiceFilter` fala por strings, e `activeAccountsOnly` e um booleano: esta e a
 * palavra que traduz um no outro, nos dois sentidos, num lugar so. Nao viaja no
 * endereco - la o recorte continua sendo `ck=1`, que e de `CollaboratorsKeys`.
 */
const SO_ATIVAS = 'ativas';
/**
 * O que o cartao de filtros NAO mostra, dito em palavras.
 *
 * As duas datas de atividade sairam daqui quando o seletor de periodo entrou no cartao:
 * dizer no aviso o que o controle ao lado ja diz e a mesma informacao duas vezes, e foi
 * por nao ter aquele seletor que este aviso precisou nascer. Sobrou o que nenhum
 * controle desta tela expressa, e que muda o numero da lista:
 *
 * Sobrou UM: o recorte de TREINO, que estreita "registrou algo" para "treinou" e que
 * nenhum controle desta tela expressa.
 *
 * E entrou outro em 08/10/2026: o PERIODO DA FAIXA, que chega da barra do Indice de saude
 * geral. O combo de faixa diz qual faixa; so este aviso diz que ela e a da ultima medicao
 * daquele periodo, e nao a de hoje - e e isso que faz a lista bater com a barra.
 *
 * Os outros tres foram saindo daqui conforme ganharam controle, todos pela mesma regra -
 * o que um controle ao lado ja diz nao precisa ser dito aqui:
 *
 * - as datas de ATIVIDADE, quando o seletor de periodo entrou no cartao de filtros;
 * - o CADASTRO ATIVO, quando virou o combo "CADASTRO" na fileira;
 * - o periodo de CADASTRO, quando ganhou o seletor "CADASTRADO EM" ao lado do outro.
 *
 * Com isso os cartoes "Ativos (N dias)" e "Novos cadastros" deixaram de produzir aviso -
 * e esta certo, porque tudo o que eles mandam esta na fileira, visivel e reversivel.
 */
function describeContext(criteria) {
    const trechos = [];
    if (criteria.activityKind === 'WORKOUT') {
        trechos.push('Somente quem treinou');
    }
    // So com o filtro de faixa: sem ele o periodo nao recorta nada.
    if (criteria.measurementTypeId && (criteria.statusFrom || criteria.statusTo)) {
        const { statusFrom: from, statusTo: to } = criteria;
        trechos.push(from && to
            ? `Faixa pela última medição entre ${formatDate(from)} e ${formatDate(to)}`
            : from
                ? `Faixa pela última medição a partir de ${formatDate(from)}`
                : `Faixa pela última medição até ${formatDate(to)}`);
    }
    return trechos.length > 0 ? trechos.join(' · ') : undefined;
}
/**
 * A lista de colaboradores.
 *
 * A busca vai ao servidor; a ORDENACAO e a PAGINACAO acontecem aqui, sobre o que
 * voltou. Nao e escolha de arquitetura, e do backend: o payload aceita filtro e
 * tamanho de pagina, mas nao criterio de ordenacao - e ordenar 14 colunas
 * corretamente exige ter a base toda em maos. O painel antigo faz igual, com a
 * diferenca de que la nada disso aparece no endereco.
 */
export class CollaboratorListPresenter extends CubePresenter {
    /**
     * O modelo da tabela - do CONCEITO, que sobrevive a ida ao detalhe. A listagem sempre
     * esta debaixo dele na arvore; montada sem ele, a fiacao esta quebrada, e falhar alto
     * e melhor que criar em silencio um segundo modelo, sem a escolha gravada antes.
     */
    get table() {
        if (!this.concept) {
            throw new Error('A listagem de Colaboradores sem o conceito acima dela');
        }
        return this.concept.table;
    }
    constructor(app) {
        super(app, new CollaboratorsScope());
        this.parentSlot = NOOP_VOID;
        /** Quem publica o filtro no endereco - ver `CollaboratorsPresenter`. */
        /**
         * O periodo escolhido no seletor.
         *
         * Ele aplica NA HORA, como os outros seletores da barra: nao ha o que "parar de
         * escolher". Quem espera sao os campos de texto, por 400ms - ver `handleTextChanged`.
         */
        this.escolhido = { from: '', to: '' };
        /**
         * O seletor de periodo do cartao de filtros.
         *
         * Ele controla `activeFrom`/`activeTo`, que e o mesmo par que os cartoes de Adocao e
         * Engajamento mandam - foi assim que aquele recorte deixou de ser invisivel: quem
         * chega de um cartao ve as duas datas preenchidas no filtro, no lugar onde se procura
         * por elas, e pode mexer.
         */
        this.period = new PeriodSelectorPresenter(this.update, COLLABORATOR_SHORTCUTS, async ({ from, to, shortcut }) => {
            this.escolhido = { from, to };
            // O seletor AVISA a escolha; quem a desenha e o `show` - ver a nota em
            // `MonitoringListPresenter`, onde isto foi aprendido.
            this.period.show(shortcut, from, to);
            // Escolha discreta, sem espera: nao ha o que "parar de digitar".
            await this.aplicar();
        });
        /** O periodo de CADASTRO escolhido no seletor. */
        this.escolhidoCadastro = { from: '', to: '' };
        /**
         * O seletor do periodo de CADASTRO - "quem entrou na base entre estas datas".
         *
         * Irmao do de cima, e nao o mesmo: sao dois eixos diferentes no servidor
         * (`activeBetween` e `registeredBetween`), e um seletor so nao conseguiria dizer os
         * dois ao mesmo tempo - "cadastrados em setembro E que registraram algo na semana
         * passada" e uma pergunta legitima, e com um par de datas ela nao existe.
         *
         * Nasceu quando "Novos cadastros" precisou de caminho: aquele recorte ja chegava dos
         * cartoes e so aparecia como frase no aviso acima da tabela, sem ninguem poder mexer.
         */
        this.registration = new PeriodSelectorPresenter(this.update, COLLABORATOR_SHORTCUTS, async ({ from, to, shortcut }) => {
            this.escolhidoCadastro = { from, to };
            this.registration.show(shortcut, from, to);
            await this.aplicar();
        });
        this.at = { criteria: {}, page: 1 };
        /**
         * Medicoes incluidas pelo seletor de colunas enquanto ha um indicador em FOCO.
         *
         * Valem so para aquele foco: chegar por outra barra do Dashboard volta a mostrar so
         * o indicador dela, como decidido em 14/10/2026.
         */
        this.focusExtras = new Set();
        /** O seletor de empresa - ver `CompanySelectorPresenter`. */
        this.selector = new CompanySelectorPresenter(this.update, async (companyId) => {
            this.owner?.rememberFilterCompany(companyId);
            await this.go({ criteria: { ...this.at.criteria, companyId }, page: 1 });
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /** O que voltou do servidor, antes de ordenar e paginar. */
        this.all = [];
        this.pending = 0;
    }
    async applyParameters(intent, initialization) {
        const keys = new CollaboratorListKeys(this.app, intent);
        /*
         * A guarda de papel, que no painel antigo era o `ProtectRouter` de cada rota.
         *
         * Fica na LISTAGEM e nao no conceito: o profissional nao tem esta tela, mas
         * abre o DETALHE por link direto, e o detalhe e irmao daqui. Guardar o
         * conceito fecharia os dois.
         *
         * Sem `last`: a listagem so e instanciada quando E o destino - quem para no
         * conceito e reencaminhado para ca, e o detalhe nao passa por aqui.
         */
        if (!(await keys.owner?.allowEntry('collaborators', intent))) {
            return false;
        }
        this.concept = keys.concept;
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onNameChanged = this.handleTextChanged.bind(this, 'name');
            this.scope.onCpfChanged = this.handleTextChanged.bind(this, 'cpf');
            this.scope.onEmailChanged = this.handleTextChanged.bind(this, 'email');
            this.scope.onSubmit = this.action(this.onSubmit);
            this.scope.onClearFilters = this.action(this.onClearFilters);
            this.scope.onClearContext = this.action(this.onClearContext);
            this.scope.onPreviousPage = this.action(this.onMovePage.bind(this, -1));
            this.scope.onNextPage = this.action(this.onMovePage.bind(this, 1));
            this.scope.onGoToPage = this.action(this.onGoToPage);
            // O scope do seletor e o MESMO objeto por toda a vida da tela: entregue uma
            // vez, como nos Monitoramentos. Reatribui-lo a cada `showRequest` e escrever
            // num `@observe()` dentro do ciclo de update que aquela escrita provocou.
            this.scope.period = this.period.scope;
            this.scope.registrationPeriod = this.registration.scope;
            // Quem tem mais de uma empresa escolhe entre elas; o admin geral, entre todas.
            if (this.canChooseCompany) {
                this.selector.describe('EMPRESA', 'Todas as Empresas');
                /*
                 * Quem administra empresas escolhe entre as DELE; o administrador
                 * geral escolhe entre todas, e ai o seletor busca no servidor.
                 */
                const minhas = keys.owner?.myCompanies ?? [];
                if (minhas.length > 0) {
                    this.selector.restrictTo(minhas);
                }
                await this.selector.showSelected(keys.owner?.companyOnArrival(keys.companyId));
            }
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        const target = {
            criteria: {
                name: keys.name,
                cpf: keys.cpf,
                email: keys.email,
                // Sem empresa no endereco, a PADRAO de quem esta logado: quem
                // administra mais de uma precisa dizer qual, e o servidor recusa
                // agir sem saber.
                companyId: keys.owner?.companyOnArrival(keys.companyId),
                measurementTypeId: keys.measurementTypeId,
                measurementStatusLevel: keys.statusLevel,
                /*
                 * Os recortes que chegam dos cartoes de Adocao e Engajamento.
                 *
                 * Lidos AQUI tambem, e nao so no conceito: o conceito guarda o estado
                 * comum das duas telas, mas quem consulta e monta o aviso e esta - e
                 * foi por faltarem aqui que o primeiro clique num cartao abriu a lista
                 * inteira, sem recorte e sem dizer nada.
                 */
                activeFrom: keys.activeFrom,
                activeTo: keys.activeTo,
                activityKind: keys.activityKind,
                activityPresence: keys.activityPresence,
                registeredFrom: keys.registeredFrom,
                registeredTo: keys.registeredTo,
                statusFrom: keys.statusFrom,
                statusTo: keys.statusTo,
                activeAccountsOnly: keys.activeAccountsOnly ? true : undefined,
                anamnese: keys.anamnese
            },
            sort: keys.sort,
            page: keys.page
        };
        this.owner?.showSection('collaborators', 'Colaboradores', 'Lista de colaboradores cadastrados no sistema com dados e últimas medições');
        this.parentSlot(this.scope);
        this.scope.layout = this.table.show(this);
        await this.moveTo(target);
        return true;
    }
    /*
     * Sem `publishParameters`: o filtro, a ordenacao e a pagina sao do CONCEITO, e
     * publicar aqui tambem daria dois donos ao mesmo nome. Esta tela le e escreve
     * por `go()`, e o conceito - que esta no caminho dela e do detalhe - e quem
     * devolve os valores ao endereco.
     */
    /**
     * Administrador GERAL - e isto decide o ENDPOINT.
     *
     * `/patient/all_patients_with_last_measurement` exige `ROLE_ADMIN`; quem
     * administra empresas passa por `/patient/admin/company-employees`. Por um
     * momento este getter respondeu `canChooseCompany`, e ai um administrador de DUAS
     * empresas - que agora escolhe - caia no endpoint do super admin e levava 403,
     * que o painel le como sessao perdida e expulsa para o login.
     *
     * Sao duas perguntas diferentes que davam a MESMA resposta enquanto um admin de
     * empresa tinha uma so: "posso escolher empresa?" e "sou o administrador geral?".
     * Deixaram de dar.
     */
    get isSuperAdmin() {
        return this.owner?.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ?? false;
    }
    /** Se ha empresa a escolher - e o que decide mostrar o seletor. */
    get canChooseCompany() {
        // So quando a escolha NAO esta no cabecalho.
        return (this.owner?.canChooseCompany ?? false) && !(this.owner?.hasHeaderChooser ?? false);
    }
    async go(change) {
        await this.moveTo({ ...this.at, ...change });
        // O dono dos parametros e o conceito; sem isto o `updateHistory` publicaria
        // o estado anterior e a ordenacao nao chegaria ao endereco.
        this.concept?.moveTo(this.at);
        this.updateHistory();
    }
    /**
     * Mostra `target`, e torna-se ele quando as linhas estao em maos.
     *
     * Ordenar e paginar nao falam com a rede: se so isso mudou, a assinatura da
     * consulta e a mesma e o que esta em maos responde. E por isso que trocar de
     * pagina e instantaneo.
     */
    async moveTo(target) {
        this.showRequest(target);
        const signature = JSON.stringify([target.criteria, this.isSuperAdmin]);
        if (signature !== this.lastQuery) {
            const token = ++this.pending;
            this.scope.loading = true;
            this.scope.error = undefined;
            try {
                const page = await api.fetchCollaborators(target.criteria, this.isSuperAdmin ? 'all' : 'company');
                if (token !== this.pending) {
                    return;
                }
                this.lastQuery = signature;
                this.all = page.elements ?? [];
            }
            catch (caught) {
                if (token !== this.pending) {
                    return;
                }
                this.scope.error =
                    caught instanceof Error ? caught.message : 'Não foi possível buscar os colaboradores.';
                this.showRequest(this.at);
                this.scope.loading = false;
                return;
            }
            finally {
                if (token === this.pending) {
                    this.scope.loading = false;
                }
            }
        }
        this.at = target;
        this.showRows(target);
    }
    /** O que o leitor pediu, na tela antes da resposta chegar. */
    showRequest(target) {
        const criteria = target.criteria;
        /*
         * Os campos de texto so sao reescritos quando a mudanca veio de FORA.
         *
         * Eles sao nao controlados, e reescrever o que a propria tela acabou de enviar
         * devolveria o campo ao valor anterior no meio da digitacao - ver `enviado`.
         */
        if (criteria.name !== this.enviado?.name) {
            this.scope.name = criteria.name ?? '';
            this.scope.formRevision++;
        }
        if (criteria.cpf !== this.enviado?.cpf) {
            this.scope.cpf = criteria.cpf ?? '';
            this.scope.formRevision++;
        }
        if (criteria.email !== this.enviado?.email) {
            this.scope.email = criteria.email ?? '';
            this.scope.formRevision++;
        }
        /*
         * As duas datas desenhadas no seletor, pela mesma regra dos campos de texto.
         *
         * O ATALHO e deduzido delas, por `shortcutOf`. So as datas viajam no endereco -
         * publicar atalho e datas abriria a porta para um endereco em que os dois
         * discordam -, e sem deduzir, tudo que chegasse de fora aparecia como "Escolher
         * datas": quem clicava em "Ativos (30 dias)" via duas datas cruas e nao tinha
         * como saber, olhando a tela, que estava vendo justamente os ultimos 30 dias.
         *
         * Deduzir tambem conserta o caso que motivou o `custom` fixo de antes: escolher
         * "Últimos 30 dias" produz o intervalo dos ultimos 30 dias, que e o que
         * `shortcutOf` reconhece - o rotulo fica de pe em vez de virar "Escolher datas"
         * no instante seguinte a escolha.
         *
         * `custom` continua sendo a resposta quando nada casa, e e o caso comum: a janela
         * de um cartao termina no fim do periodo APURADO, que so e hoje quando o
         * relatorio esta aberto no periodo corrente.
         */
        const periodoDeFora = 
        // Na primeira abertura nada foi enviado, e o seletor precisa nascer desenhado.
        !this.enviado ||
            criteria.activeFrom !== this.enviado.activeFrom ||
            criteria.activeTo !== this.enviado.activeTo;
        if (periodoDeFora && this.desenhado !== `${criteria.activeFrom ?? ''}|${criteria.activeTo ?? ''}`) {
            this.desenhado = `${criteria.activeFrom ?? ''}|${criteria.activeTo ?? ''}`;
            this.escolhido = { from: criteria.activeFrom ?? '', to: criteria.activeTo ?? '' };
            this.drawPeriod(this.period, criteria.activeFrom, criteria.activeTo);
        }
        // O eixo de CADASTRO, pela mesma regra e com as mesmas guardas.
        const cadastroDeFora = !this.enviado ||
            criteria.registeredFrom !== this.enviado.registeredFrom ||
            criteria.registeredTo !== this.enviado.registeredTo;
        if (cadastroDeFora &&
            this.desenhadoCadastro !== `${criteria.registeredFrom ?? ''}|${criteria.registeredTo ?? ''}`) {
            this.desenhadoCadastro = `${criteria.registeredFrom ?? ''}|${criteria.registeredTo ?? ''}`;
            this.escolhidoCadastro = { from: criteria.registeredFrom ?? '', to: criteria.registeredTo ?? '' };
            this.drawPeriod(this.registration, criteria.registeredFrom, criteria.registeredTo);
        }
        this.buildFilters(target);
        this.buildColumns(target);
    }
    /**
     * Poe um intervalo num dos dois seletores, com o nome do atalho que o produz.
     *
     * Vale para os dois eixos porque a regra e a mesma: sem pontas e "Todo o periodo", e
     * com pontas o atalho e DEDUZIDO delas - so as datas viajam no endereco.
     */
    drawPeriod(seletor, from, to) {
        /*
         * UMA ponta ja e recorte.
         *
         * Era `from && to`, e os recortes ABERTOS ficavam invisiveis: "Ja sincronizaram o
         * wearable" e "Total de colaboradores" mandam so o teto ("ate 11/09"), e o seletor
         * desenhava "Todo o periodo" sobre uma lista ja filtrada - exatamente o que estes
         * controles existem para evitar. `shortcutOf` responde `custom` para meia ponta, que
         * e a leitura honesta: nenhum atalho nomeia "tudo ate uma data".
         */
        if (from || to) {
            seletor.show(shortcutOf(from ?? '', to ?? '', COLLABORATOR_SHORTCUTS), from, to);
        }
        else {
            seletor.show('all');
        }
    }
    /** As linhas da pagina atual, ordenadas como o endereco pede. */
    showRows(target) {
        const ordered = this.sorted(this.all, target.sort);
        this.scope.total = ordered.length;
        const pageCount = Math.max(1, Math.ceil(ordered.length / this.scope.perPage));
        const page = Math.min(Math.max(1, target.page), pageCount);
        // Uma pagina alem do fim - por link antigo ou filtro que encolheu a lista -
        // e corrigida aqui, e o endereco segue o que esta na tela.
        if (page !== target.page) {
            this.at = { ...target, page };
        }
        this.scope.page = page;
        const start = (page - 1) * this.scope.perPage;
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.rows = reuseRows(this.scope.rows, ordered.slice(start, start + this.scope.perPage), (patient) => patient.patientId, (patient, reuse) => this.buildRow(patient, reuse));
    }
    sorted(patients, sort) {
        this.table.setSort(sort ?? DEFAULT_SORT);
        return this.table.sortRows(patients);
    }
    buildRow(patient, reuse) {
        const href = this.app.sealedAddress(CollaboratorDetailKeys.plainAddressOf(patient.patientId));
        return buildCollaboratorRow(patient, this.update, () => this.onOpenCollaborator(patient), href, reuse, this.sleepLinkOf(patient));
    }
    /** A celula de sono leva ao Relatorio de Sono da pessoa - ver `sleepReportLinkOf`. */
    sleepLinkOf(patient) {
        const link = sleepReportLinkOf(this.app, this.owner, patient);
        return link && { ...link, open: this.action(link.open) };
    }
    /**
     * As colunas de medicao (B-42).
     *
     * Sem foco, as que a pessoa deixou visiveis, na ordem que ela arrastou. Com um
     * indicador em foco - a lista aberta pela barra do Dashboard, ou filtrada por uma
     * medicao -, so a coluna dele, logo depois do nome, mais as que a pessoa incluir.
     */
    buildColumns(target) {
        const typeId = target.criteria.measurementTypeId;
        const candidate = typeId === undefined ? undefined : `m${typeId}`;
        const focus = candidate && MEASUREMENT_FIELDS.includes(candidate) ? candidate : undefined;
        if (focus !== this.lastFocus) {
            this.focusExtras.clear();
            this.lastFocus = focus;
        }
        const preferences = this.table.preferences;
        const arranged = preferences.arrange(MEASUREMENT_FIELDS);
        const visible = focus
            ? arranged.filter((field) => this.focusExtras.has(field))
            : arranged.filter((field) => !preferences.isHidden(field));
        this.scope.columns = buildCollaboratorColumns(target.sort, this.update, (field) => this.onSort(field), {
            measurements: visible,
            focus,
            onDrop: (source, targetField) => {
                if (preferences.move(MEASUREMENT_FIELDS, source, targetField)) {
                    this.buildColumns(this.at);
                }
            }
        }, this.scope.columns);
        this.table.setVisible(this.scope.columns.map((column) => column.field));
        this.scope.columnChoices = arranged.map((field) => {
            const choice = new ColumnChoiceScope();
            choice.identity = field;
            choice.field = field;
            choice.label =
                this.scope.columns.find((column) => column.field === field)?.label ??
                    measurementLabelOf(Number(field.slice(1)));
            choice.locked = field === focus;
            choice.checked = field === focus || visible.includes(field);
            choice.onToggle = this.action(async () => {
                if (focus) {
                    if (this.focusExtras.has(field))
                        this.focusExtras.delete(field);
                    else
                        this.focusExtras.add(field);
                }
                else {
                    preferences.toggle(field);
                }
                this.buildColumns(this.at);
            });
            choice.update = this.update;
            return choice;
        });
    }
    buildFilters(target) {
        const criteria = target.criteria;
        if (this.canChooseCompany) {
            this.scope.companyFilter = this.selector.scope;
        }
        this.scope.measurementTypeFilter = this.buildFilter('TIPO DE MEDIÇÃO', 'Todos', criteria.measurementTypeId === undefined ? undefined : String(criteria.measurementTypeId), MEASUREMENT_TYPES.filter((type) => type.hasStatus).map((type) => ({
            label: type.label,
            value: String(type.id)
        })), (value) => ({
            criteria: {
                ...criteria,
                measurementTypeId: value === undefined ? undefined : Number(value),
                // A faixa nao faz sentido sem indicador: limpar o indicador limpa
                // a faixa. O painel antigo precisa de um `useEffect` para isso.
                measurementStatusLevel: value === undefined ? undefined : criteria.measurementStatusLevel
            },
            page: 1
        }));
        const statusFilter = this.buildFilter('STATUS DE SAÚDE', 'Todos', criteria.measurementStatusLevel, STATUS_ORDER.map((level) => ({ label: STATUS_LABELS[level], value: level })), (value) => ({
            criteria: { ...criteria, measurementStatusLevel: value },
            page: 1
        }));
        statusFilter.enabled = criteria.measurementTypeId !== undefined;
        this.scope.statusFilter = statusFilter;
        /*
         * CADASTRO - o unico filtro desta tela que nao pergunta por um VALOR, e sim liga
         * ou desliga um recorte.
         *
         * Combo e nao um liga-desliga: a fileira inteira e de combos, e um interruptor
         * ali seria o unico controle com outra forma - e a forma diz o que se espera
         * dele. Tambem ganha de graca o "x" que limpa e o rotulo em cima, que e como os
         * vizinhos se comportam.
         *
         * Uma opcao so na lista, e "Todos" como PLACEHOLDER e nao como opcao: e a
         * convencao do `ChoiceFilter` - o vazio e a ausencia de filtro, e quem limpa e o
         * "x". Ver a nota la.
         *
         * "CADASTRO: Somente ativos" e nao "CONTAS: Somente ativas" porque e o termo que
         * Adocao e Engajamento ja usa duas vezes - "108 com cadastro ativo" e "Base
         * considerada: usuarios com cadastro ativo". As duas telas falam da mesma coisa,
         * e falar diferente faria parecer que sao duas.
         */
        this.scope.accountsFilter = this.buildFilter('CADASTRO', 'Todos', criteria.activeAccountsOnly ? SO_ATIVAS : undefined, [{ label: 'Somente ativos', value: SO_ATIVAS }], (value) => ({
            criteria: { ...criteria, activeAccountsOnly: value === SO_ATIVAS ? true : undefined },
            page: 1
        }));
        /*
         * ANAMNESE - os dois lados de um degrau do funil, e nao um liga-desliga.
         *
         * "Preenchida" e "sem preencher" sao perguntas simetricas e as duas tem cartao em
         * Adocao e Engajamento, entao as duas sao opcao. Diferente de CADASTRO ao lado,
         * que so tem um lado que interessa - ninguem procura "somente desativados".
         *
         * E da FICHA, e nao da conta: `patient.anamnese_id`. Quem tem duas fichas aparece
         * na lista com a que falta, e conta uma vez so no cartao - a divergencia esta
         * escrita em `FiltroDeAnamneseBateComOFunilTest`, no servidor.
         */
        this.scope.anamneseFilter = this.buildFilter('ANAMNESE', 'Todas', criteria.anamnese, [
            { label: 'Preenchida', value: 'FILLED' },
            { label: 'Sem preencher', value: 'MISSING' }
        ], (value) => ({
            criteria: { ...criteria, anamnese: value },
            page: 1
        }));
        /*
         * ATIVIDADE - o que o periodo ao lado quer dizer.
         *
         * Nao e um filtro a mais sobre o periodo: e o SENTIDO dele. "Registrou" e o padrao
         * e por isso e o placeholder, como "Todos" nos vizinhos - limpar o combo volta a
         * ler o periodo como presenca.
         *
         * "Parou" precisa dos dois recortes ao mesmo tempo (nada no periodo, e algo antes
         * dele), e quem os monta e a `.api.ts`. Aqui ele e uma escolha so, porque para quem
         * le a tela tambem e uma pergunta so.
         */
        this.scope.activityFilter = this.buildFilter('ATIVIDADE', 'Registrou', criteria.activityPresence, [
            { label: 'Não registrou', value: 'ABSENT' },
            { label: 'Parou (registrou antes)', value: 'STALLED' }
        ], (value) => ({
            criteria: { ...criteria, activityPresence: value },
            page: 1
        }));
        // O rotulo do seletor vizinho segue o modo - ver `CollaboratorsScope.periodLabel`.
        this.scope.periodLabel = criteria.activityPresence === undefined ? 'Registrou em' : 'Sem registro em';
        this.scope.anyFilterActive = Object.keys(criteria).some((key) => criteria[key] !== UNFILTERED[key]);
        this.scope.contextNotice = describeContext(criteria);
    }
    buildFilter(label, placeholder, current, options, target) {
        const filter = new FilterScope();
        filter.identity = label;
        filter.label = label;
        filter.placeholder = placeholder;
        filter.active = current !== undefined;
        filter.value = current ?? '';
        filter.summary = options.find((option) => option.value === current)?.label ?? placeholder;
        filter.onClear = this.action(async () => {
            await this.go(target(undefined));
        });
        filter.update = this.update;
        filter.options = options.map((option) => {
            const scope = new FilterOptionScope();
            scope.identity = `${label}:${option.value ?? ''}`;
            scope.label = option.label;
            scope.value = option.value;
            scope.current = option.value === current;
            scope.onSelect = this.action(async () => {
                /*
                 * Escolher o que JA esta escolhido nao e viagem.
                 *
                 * O `Select` redispara `onChange` com a mesma escolha quando a lista de
                 * opcoes chega nova - e ela chega nova a cada `showRequest`. Sem esta
                 * guarda o redisparo volta para ca, navega, refaz a lista e redispara:
                 * medido em 20/09/2026, 8.608 chamadas com o mesmo valor em dois
                 * segundos, ate o navegador recusar requisicao
                 * (`ERR_INSUFFICIENT_RESOURCES`) e o Solid estourar a pilha.
                 *
                 * A raiz foi tratada no `ChoiceFilter`, que passou a ser CONTROLADO pelo
                 * scope; esta guarda fica como a segunda tranca, e porque ela e correta
                 * por si: uma acao de scope que navega para o lugar onde ja se esta erra
                 * venha o clique de onde vier.
                 */
                if (option.value === current) {
                    return;
                }
                await this.go(target(option.value));
            });
            scope.update = this.update;
            return scope;
        });
        return filter;
    }
    // ========== ACOES ==========
    /**
     * Espelha o digitado, e AGENDA a busca.
     *
     * O filtro vale sozinho, como em Empresas e nos Monitoramentos: o texto depois de
     * parar de digitar, os seletores na hora. O "Aplicar busca" saiu com isto - as tres
     * listas do painel se comportam igual, e o botao era a unica diferenca que restava.
     *
     * A espera existe porque sem ela seriam sete consultas para escrever "Alexandre", e
     * o endereco ganharia sete entradas no historico: o Voltar passaria a desfazer letra
     * por letra.
     */
    handleTextChanged(field, value) {
        // O CPF ganha mascara enquanto se digita, como no filtro da referencia
        // (`handleMasks(text, 'cpf')`). O porte mostrava os digitos crus.
        this.scope[field] = field === 'cpf' ? maskCpf(value) : value;
        clearTimeout(this.espera);
        this.espera = setTimeout(() => void this.aplicar(), BUSCA_DEBOUNCE_MS);
    }
    /** O Enter no formulario busca AGORA, sem esperar o resto da digitacao. */
    async onSubmit() {
        await this.aplicar();
    }
    /**
     * Manda o que esta na tela para o endereco, e com ele para o servidor.
     *
     * O que NAO esta na tela vai junto e nao se perde: a empresa do cabecalho, e os
     * recortes que chegaram de um cartao de Adocao e Engajamento e que nenhum controle
     * daqui expressa. Montar o criterio so com o que os campos dizem foi como, nos
     * Monitoramentos, filtrar por nome passou a trazer colaborador de outra empresa.
     */
    async aplicar() {
        clearTimeout(this.espera);
        const criteria = {
            ...this.at.criteria,
            name: this.scope.name || undefined,
            cpf: this.scope.cpf || undefined,
            email: this.scope.email || undefined,
            activeFrom: this.escolhido.from || undefined,
            activeTo: this.escolhido.to || undefined,
            registeredFrom: this.escolhidoCadastro.from || undefined,
            registeredTo: this.escolhidoCadastro.to || undefined
        };
        this.enviado = criteria;
        await this.go({ criteria, page: 1 });
    }
    /**
     * Esvazia os filtros E busca - as duas coisas, como nas outras duas listas.
     *
     * Antes ele so esvaziava: era o `reset()` do formulario da referencia, que fazia
     * sentido quando a busca esperava um botao. Sem o botao, deixar a lista recortada
     * depois de o leitor pedir para limpar seria nao atender o pedido.
     *
     * O que veio de FORA tambem sai: um "limpar" que deixasse o recorte do cartao de
     * Adocao e Engajamento em pe explicaria menos ainda do que ele ja explica.
     */
    async onClearFilters() {
        this.escolhido = { from: '', to: '' };
        this.escolhidoCadastro = { from: '', to: '' };
        this.scope.name = '';
        this.scope.cpf = '';
        this.scope.email = '';
        await this.go({ criteria: { ...UNFILTERED, companyId: this.at.criteria.companyId }, page: 1 });
    }
    /**
     * Sai do recorte que veio do cartao - o que a barra de filtros nao expressa.
     *
     * O "Limpar" da barra tira TUDO, inclusive o que os controles mostram; este tira so
     * o que chegou de fora, e e por isso que os dois convivem: quem clicou em "Ver
     * todos" quer a lista inteira sem perder o nome que acabou de digitar.
     */
    async onClearContext() {
        await this.go({
            criteria: {
                ...this.at.criteria,
                activeFrom: undefined,
                activeTo: undefined,
                activityKind: undefined,
                registeredFrom: undefined,
                registeredTo: undefined,
                statusFrom: undefined,
                statusTo: undefined,
                activeAccountsOnly: undefined
            },
            page: 1
        });
    }
    async onSort(field) {
        const current = this.at.sort;
        const sort = current === field ? `-${field}` : current === `-${field}` ? undefined : field;
        await this.go({ sort, page: 1 });
    }
    /** Ir direto a uma pagina - o paginador numerado da referencia tem isso. */
    async onGoToPage(page) {
        const alvo = Math.min(Math.max(1, page), this.scope.pageCount);
        if (alvo !== this.at.page) {
            await this.go({ page: alvo });
        }
    }
    async onMovePage(direction) {
        const page = Math.min(Math.max(1, this.at.page + direction), this.scope.pageCount);
        if (page !== this.at.page) {
            await this.go({ page });
        }
    }
    /**
     * Abre o detalhe de um colaborador.
     *
     * Os filtros, a ordenacao e a pagina viajam no intent porque
     * `CollaboratorDetailKeys` herda de `CollaboratorsKeys` - e e isso que faz o
     * botao Voltar do detalhe cair na MESMA lista filtrada, sem ninguem guardar
     * nada em variavel.
     *
     * A linha vai como atributo, fora da URL: o cabecalho do detalhe aparece
     * preenchido de imediato em vez de piscar vazio enquanto os dados completos
     * chegam.
     */
    async onOpenCollaborator(patient) {
        // `of` e nao copia campo a campo: o detalhe e um place DENTRO desta lista,
        // e o framework pergunta a cada presenter vivo o que ele mostra - o filtro
        // que viaja e o que esta na tela. So o paciente e novidade.
        const target = CollaboratorDetailKeys.of(this.app);
        target.patientId = patient.patientId;
        target.row = patient;
        await target.flip();
    }
}
//# sourceMappingURL=collaborator-list.presenter.js.map